import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag, Switch,
  Space, Popconfirm, message, Card, Typography, Badge, Tooltip
} from 'antd';
import { PlusOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { getBlockRules, addBlockRule, updateBlockRule, deleteBlockRule } from '../services/api';

const { Text } = Typography;

// All supported dimensions
const DIMENSIONS = [
  { key: 'timezone', label: '时区', placeholder: '如: Asia/Shanghai' },
  { key: 'platform', label: '平台', placeholder: '如: Win32, MacIntel, Linux x86_64' },
  { key: 'screen', label: '屏幕分辨率', placeholder: '如: 1920x1080' },
  { key: 'language', label: '语言', placeholder: '如: zh-CN, en-US' },
  { key: 'hardware_concurrency', label: 'CPU核心数', placeholder: '如: 4, 8' },
  { key: 'device_memory', label: '设备内存(GB)', placeholder: '如: 4, 8' },
  { key: 'max_touch_points', label: '触控点数', placeholder: '如: 0, 5, 10' },
  { key: 'webgl_renderer', label: 'WebGL渲染器', placeholder: '如: ANGLE (Intel...)' },
  { key: 'webgl_vendor', label: 'WebGL厂商', placeholder: '如: Google Inc.' },
  { key: 'audio_hash', label: '音频指纹', placeholder: '音频指纹哈希值' },
  { key: 'pixel_ratio', label: '像素比', placeholder: '如: 1, 2, 3' },
  { key: 'user_agent', label: 'User-Agent', placeholder: 'UA字符串' },
  { key: 'canvas', label: 'Canvas指纹', placeholder: 'Canvas哈希值' },
  { key: 'canvas_contains', label: 'Canvas包含', placeholder: '输入Canvas中包含的子串', matchType: 'contains' },
];

// Dimensions that use "contains" match mode
const CONTAINS_DIMS = new Set(['canvas_contains']);

const dimLabelMap = {};
DIMENSIONS.forEach(d => { dimLabelMap[d.key] = d.label; });

function BlockRulesPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [ruleName, setRuleName] = useState('');
  const [ruleReason, setRuleReason] = useState('');
  const [conditions, setConditions] = useState([{ key: 'timezone', value: '' }]);

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getBlockRules({ page, page_size: 50 });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRuleName('');
    setRuleReason('');
    setConditions([{ key: 'timezone', value: '' }]);
  };

  const handleAdd = async () => {
    if (!ruleName.trim()) {
      message.warning('请输入规则名称');
      return;
    }
    // Build conditions object
    const condObj = {};
    let hasValid = false;
    for (const c of conditions) {
      if (c.key && c.value.trim()) {
        condObj[c.key] = c.value.trim();
        hasValid = true;
      }
    }
    if (!hasValid) {
      message.warning('至少需要一个有效条件');
      return;
    }

    setSubmitting(true);
    try {
      await addBlockRule({ name: ruleName.trim(), conditions: condObj, reason: ruleReason.trim() });
      message.success('规则创建成功');
      setModalVisible(false);
      resetForm();
      loadData();
    } catch (err) {
      message.error(err.response?.data?.error || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (record, checked) => {
    try {
      await updateBlockRule(record.id, { is_active: checked });
      message.success(checked ? '已启用' : '已禁用');
      loadData();
    } catch (err) {
      message.error('操作失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteBlockRule(id);
      message.success('已删除');
      loadData();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const addCondition = () => {
    // Find first unused dimension
    const used = new Set(conditions.map(c => c.key));
    const available = DIMENSIONS.find(d => !used.has(d.key));
    if (available) {
      setConditions([...conditions, { key: available.key, value: '' }]);
    } else {
      message.warning('已添加所有可用维度');
    }
  };

  const removeCondition = (idx) => {
    if (conditions.length <= 1) return;
    setConditions(conditions.filter((_, i) => i !== idx));
  };

  const updateCondition = (idx, field, val) => {
    const updated = [...conditions];
    updated[idx] = { ...updated[idx], [field]: val };
    setConditions(updated);
  };

  const columns = [
    {
      title: '规则名',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (val) => <Text strong style={{ fontSize: 13 }}>{val}</Text>
    },
    {
      title: '条件',
      dataIndex: 'conditions',
      key: 'conditions',
      render: (conds) => {
        if (!conds || typeof conds !== 'object') return '-';
        return (
          <Space wrap size={[4, 4]}>
            {Object.entries(conds).map(([k, v]) => {
              const isEmptyVal = String(v).toUpperCase() === '__EMPTY__';
              const isContains = CONTAINS_DIMS.has(k);
              const operator = isEmptyVal ? '= 为空' : isContains ? '⊃ ' + v : '= ' + v;
              const tagColor = isEmptyVal ? 'orange' : isContains ? 'green' : 'blue';
              return (
                <Tooltip key={k} title={`${dimLabelMap[k] || k} ${operator}`}>
                  <Tag color={tagColor} style={{ fontSize: 11 }}>
                    {dimLabelMap[k] || k}{isContains ? ' ⊃ ' : ': '}{isEmptyVal ? '⊘ 为空' : (String(v).length > 20 ? String(v).slice(0, 20) + '...' : v)}
                  </Tag>
                </Tooltip>
              );
            })}
          </Space>
        );
      }
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 140,
      render: (val) => <Text type="secondary" style={{ fontSize: 12 }}>{val || '-'}</Text>
    },
    {
      title: '命中',
      dataIndex: 'hit_count',
      key: 'hit_count',
      width: 70,
      render: (val) => <Badge count={val || 0} showZero overflowCount={99999}
        style={{ backgroundColor: val > 0 ? '#ff4d4f' : '#d9d9d9' }} />
    },
    {
      title: '启用',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 70,
      render: (val, record) => (
        <Switch size="small" checked={val} onChange={(c) => handleToggle(record, c)} />
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (val) => <Text type="secondary" style={{ fontSize: 11 }}>{val}</Text>
    },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_, record) => (
        <Popconfirm title="确认删除此规则？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
          <Button type="text" danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    },
  ];

  // Get available dimensions for a condition row
  const getAvailableDims = (currentIdx) => {
    const used = new Set(conditions.map((c, i) => i !== currentIdx ? c.key : null).filter(Boolean));
    return DIMENSIONS.filter(d => !used.has(d.key) || d.key === conditions[currentIdx]?.key);
  };

  return (
    <div>
      <Card size="small" bordered={false} style={{ marginBottom: 12, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <FilterOutlined style={{ color: '#1890ff' }} />
            <Text strong>组合拦截规则</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              设置多维度条件，同时满足所有条件时拦截
            </Text>
            <Badge count={total} style={{ backgroundColor: '#1890ff' }} overflowCount={9999} />
          </Space>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
            添加规则
          </Button>
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          current: page,
          pageSize: 50,
          total: total,
          size: 'small',
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p) => setPage(p)
        }}
      />

      <Modal
        title="添加组合拦截规则"
        open={modalVisible}
        onCancel={() => { setModalVisible(false); resetForm(); }}
        onOk={handleAdd}
        confirmLoading={submitting}
        okText="创建规则"
        cancelText="取消"
        width={600}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>规则名称 *</Text>
            <Input
              placeholder="如: 拦截特定虚拟机环境"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>条件 (必须同时满足所有条件才拦截)</Text>
              <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addCondition}>
                添加条件
              </Button>
            </div>
            {conditions.map((cond, idx) => {
              const dimInfo = DIMENSIONS.find(d => d.key === cond.key);
              const isEmptyMode = cond.value === '__EMPTY__';
              const isContainsDim = CONTAINS_DIMS.has(cond.key);
              return (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <Select
                    style={{ width: 160 }}
                    size="small"
                    value={cond.key}
                    onChange={(val) => updateCondition(idx, 'key', val)}
                  >
                    {getAvailableDims(idx).map(d => (
                      <Select.Option key={d.key} value={d.key}>{d.label}</Select.Option>
                    ))}
                  </Select>
                  <Text style={{ fontSize: 12, color: isContainsDim ? '#52c41a' : undefined, fontWeight: isContainsDim ? 'bold' : undefined }}>
                    {isContainsDim ? '包含' : '='}
                  </Text>
                  <div style={{ flex: 1, display: 'flex', gap: 4 }}>
                    {isEmptyMode ? (
                      <Tag color="orange" style={{ lineHeight: '24px', margin: 0, flex: 1, textAlign: 'center' }}>为空 (空值匹配)</Tag>
                    ) : (
                      <Input
                        size="small"
                        style={{ flex: 1 }}
                        placeholder={dimInfo?.placeholder || '输入值'}
                        value={cond.value}
                        onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                      />
                    )}
                    <Button
                      size="small"
                      type={isEmptyMode ? 'primary' : 'default'}
                      style={{ fontSize: 11, padding: '0 6px' }}
                      onClick={() => updateCondition(idx, 'value', isEmptyMode ? '' : '__EMPTY__')}
                    >
                      {isEmptyMode ? '输入值' : '为空'}
                    </Button>
                  </div>
                  {conditions.length > 1 && (
                    <Button type="text" danger size="small" icon={<DeleteOutlined />}
                      onClick={() => removeCondition(idx)} />
                  )}
                </div>
              );
            })}
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>拦截原因（可选）</Text>
            <Input.TextArea
              placeholder="备注拦截原因..."
              rows={2}
              value={ruleReason}
              onChange={(e) => setRuleReason(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BlockRulesPage;
